import {
  cloneObject,
  deserify,
  IconWrapper,
  IStyles,
  pxToRem,
  pxTovW,
  QuantityButtonV2,
  SelectMenu,
  theme,
} from '@geneo2-web/shared-ui';
import { Checkbox, Typography, useMediaQuery } from '@mui/material';
import { Box } from '@mui/system';
import { Question_QuestionEnum } from '@protos/content_management/content.db_pb';
import {
  MultiSubjectSubSection,
  QuestionSection,
  QuestionTypeMarks,
  SectionQuestionMetaData,
} from '@protos/learning_management/lms.db_pb';
import { useAppSelector } from '../../../../../reduxStore/reduxHooks';
import { QuestionTypeCount } from '@protos/learning_management/lms.hw.teacher.apis_pb';
import { useLocation } from 'react-router-dom';
import { useDownloadContext } from '../../../../../app/Context/DownloadContextProviderV2';
import { useDispatch } from 'react-redux';
import {
  setQPGContent,
  setQPGSettingsConfig,
  setQPGSubsectionData,
} from '../../../reducer/assessmentFlow.slice';
import {
  SubSectionQuestionType,
  SubSectionType,
} from '@protos/learning_management/lms.assessment.teacher.apis_pb';
import {
  areAllSubSectionQuestionsFilled,
  getRemainingForSubSection,
  getRemainingForSubSectionAIMode,
} from '../../QPGv2.utils';

const style: IStyles = {
  root: {
    boxSizing: 'border-box',
    width: '100%',
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'space-between',
    borderRadius: { xs: pxToRem(15), md: pxTovW(30) },
    border: '1px solid #007CDC',
    backgroundColor: '#fff',
    padding: { xs: pxToRem(20), md: pxTovW(40) },
  },
  headerReorderBox: {
    display: 'flex',
    gap: { xs: pxToRem(10), md: pxTovW(20) },
    alignItems: 'center',
  },
  reorderIcon: {
    height: { xs: pxToRem(20), md: pxTovW(37) },
    width: { xs: pxToRem(15), md: pxTovW(23) },
    cursor: 'grab',
  },
  marksAndDeleteBox: {
    display: 'flex',
    gap: { xs: pxToRem(10), md: pxTovW(20) },
    alignItems: 'flex-start',
  },
  marksDisplayBox: {
    width: { xs: pxToRem(80), md: pxTovW(130) },
    height: { xs: pxToRem(30), md: pxTovW(43) },
    borderRadius: { xs: pxToRem(10), md: pxTovW(10) },
    borderBottom: 'none',
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    backgroundColor: '#DDFAEC',
  },
  deleteIcon: {
    cursor: 'pointer',
    height: { xs: pxToRem(20), md: pxTovW(43) },
    width: { xs: pxToRem(20), md: pxTovW(43) },
  },
  questionTypeAndDetailsBox: {
    width: '100%',
    display: 'flex',
    flexDirection: { xs: 'column', lg: 'row' },
    gap: { xs: pxToRem(10), md: pxTovW(10) },
    justifyContent: 'space-between',
    alignItems: { xs: 'flex-start', lg: 'center' },
    mt: { xs: pxToRem(20), md: pxTovW(40) },
  },
  questionDetailsBox: {
    width: '100%',
    display: 'flex',
    flexDirection: { xs: 'column', md: 'row' },
    gap: { xs: pxToRem(10), md: pxTovW(20) },
    justifyContent: 'space-between',
    alignItems: { md: 'center' },
  },
  questionTypeBox: {
    width: { xs: '100%', md: 'unset' },
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'flex-start',
    gap: { xs: pxToRem(10), md: pxToRem(15), lg: pxToRem(20) },
  },
  selectMenuPlaceholderBox: {
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    fontWeight: 'bold',
    textAlign: 'left',
    fontSize: { xs: pxToRem(14), md: pxTovW(14) },
    fontFamily: 'Poppins',
  },
  selectMenuValueBox: {
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    fontWeight: 'bold',
    textAlign: 'left',
    fontSize: { xs: pxToRem(14), md: pxTovW(14) },
    fontFamily: 'Poppins',
  },
  selectMenuRootStyles: {
    height: { xs: pxToRem(40), md: pxTovW(70), lg: pxTovW(56) },
    backgroundColor: '#F5FAFF',
    border: '1px solid #55ACFF',
    '.MuiOutlinedInput-notchedOutline': {
      border: 'none',
    },
    '&:hover .MuiOutlinedInput-notchedOutline': {
      border: 'none',
    },
    '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
      border: 'none',
    },
    '&:focus-visible .MuiOutlinedInput-notchedOutline': {
      border: 'none',
    },
    '& .MuiSelect-select > em': {
      fontStyle: 'normal !important',
      fontFamily: 'Lato',
    },
    width: { xs: '100%', md: pxTovW(206) },
  },
  marksAndNoOfQuestionsBox: {
    width: { xs: '100%', md: '60%', lg: 'unset' },
    display: 'flex',
    gap: { xs: pxToRem(10), md: pxTovW(50), lg: pxToRem(20) },
    justifyContent: 'space-around',
    alignItems: 'center',
  },
  marksBox: {
    width: { xs: pxToRem(131), md: pxTovW(180) },
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'space-between',
    gap: { xs: pxToRem(10), md: pxTovW(20), lg: pxToRem(20) },
  },
  noOfQuestionsBox: {
    width: { xs: pxToRem(131), md: pxTovW(180) },
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'space-between',
    gap: { xs: pxToRem(10), md: pxTovW(20) },
  },
  addAlternateQuestionsBox: {
    display: 'flex',
    alignItems: 'center',
    gap: { xs: pxToRem(10), md: pxTovW(10) },
    // marginTop: { xs: pxToRem(20), md: pxTovW(40) },
    cursor: 'pointer',
  },
  alternateQuestionsCheckbox: {
    height: { xs: pxToRem(17), md: pxTovW(30), lg: pxTovW(24) },
    padding: { xs: pxToRem(5), md: pxTovW(8), lg: pxTovW(5) },
    width: { xs: pxToRem(17), md: pxTovW(30), lg: pxTovW(24) },
    borderRadius: { xs: pxToRem(5), md: pxTovW(5) },
  },
  alternateQuestionsTypo: {
    width: 'max-content',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
  },
  rowDeleteIcon: {
    cursor: 'pointer',
    height: { xs: pxToRem(20), md: pxTovW(24) },
    width: { xs: pxToRem(20), md: pxTovW(24) },
    flexShrink: 0,
  },
  alternateAndDeleteBox: {
    display: 'flex',
    alignItems: 'center',
    gap: { xs: pxToRem(10), md: pxTovW(20) },
    marginTop: { xs: pxToRem(20), md: pxTovW(40) },
  },
};

interface IOptionList {
  name: string;
  nonEditableItem?: boolean;
  marks?: number;
  noOfQuestions?: number;
  questionType?: Question_QuestionEnum;
}

interface ISectionProps {
  // index: number;
  // section: QuestionSection;
  updateSection: (index: number, section: QuestionSection) => void;
  deleteSection: (index: number) => void;

  subSectionDetails: SubSectionType;
  subSectionIndex: number;
  addMoreHandler: (subSectionIndex: number) => void;
}
export const QPGSubSection = (props: ISectionProps) => {
  const {
    // index,
    // section,
    updateSection,
    deleteSection,
    subSectionDetails,
    subSectionIndex,
    addMoreHandler,
  } = props;

  const MAX_MARKS_LIMIT = 20;
  const MAX_AI_QUESTIONS = 25;
  const MAX_NO_OF_QUESTIONS_LIMIT = 200;
  const location = useLocation();
  const pathSegments = location.pathname.split('/');
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const dispatch = useDispatch();
  const { isOffline } = useDownloadContext();

  const {
    qpg_settings_config,
    current_qpg_template_info,
    qpg_subsection_data,
    qpg_content,
    qpg_selected_section_index,
  } = deserify(useAppSelector((state) => state.assessment));

  // const [sectionSettings, setSectionSettings] = useState<QuestionSection>(
  //   section || new QuestionSection({})
  // );

  // ----------------------------------------------------

  const allowAiQuestions = !!qpg_settings_config?.allow_ai_questions;
  const remainingByType = allowAiQuestions
    ? getRemainingForSubSectionAIMode(
        qpg_subsection_data ?? [],
        subSectionIndex,
        MAX_AI_QUESTIONS
      )
    : getRemainingForSubSection(
        qpg_subsection_data ?? [],
        subSectionIndex,
        qpg_settings_config?.no_ofQuestions_by_types
      );

  function getMaxQuestionCountByEnum(
    enumValue?: Question_QuestionEnum,
    currentRowIndex?: number
  ): number {
    if (!enumValue) {
      return 0;
    }
    if (allowAiQuestions && typeof currentRowIndex === 'number') {
      const usedInOtherRowsInThisSubsection =
        subSectionDetails.subSectionQuestions.reduce(
          (sum, q, i) =>
            i !== currentRowIndex && q.questionType === enumValue
              ? sum + (q.noOfQuestion ?? 0)
              : sum,
          0
        );
      return Math.max(0, MAX_AI_QUESTIONS - usedInOtherRowsInThisSubsection);
    }
    const subsectionTotalRemaining = remainingByType.get(enumValue);
    if (
      subsectionTotalRemaining !== undefined &&
      subsectionTotalRemaining !== Number.MAX_SAFE_INTEGER
    ) {
      if (typeof currentRowIndex === 'number') {
        const usedInOtherRowsInThisSubsection =
          subSectionDetails.subSectionQuestions.reduce(
            (sum, q, i) =>
              i !== currentRowIndex && q.questionType === enumValue
                ? sum + (q.noOfQuestion ?? 0)
                : sum,
            0
          );
        return Math.max(
          0,
          subsectionTotalRemaining - usedInOtherRowsInThisSubsection
        );
      }
      return subsectionTotalRemaining;
    }
    if (!questionTypeList.length) {
      return 0;
    }
    const questionTypeFound = questionTypeList.find(
      (e) => e.questionType === enumValue
    );
    return questionTypeFound?.noOfQuestions || 0;
  }

  // ----------------------------------------------------

  const getMaxNoOfQuestionsLimit = (
    enumValue?: Question_QuestionEnum,
    currentRowIndex?: number
  ): number => {
    const available = getMaxQuestionCountByEnum(enumValue, currentRowIndex);
    return Math.min(MAX_NO_OF_QUESTIONS_LIMIT, available);
  };

  // ----------------------------------------------------

  const noOfQuestions = new QuestionTypeCount(
    qpg_settings_config?.no_ofQuestions_by_types
  );
  const {
    noOfMcqs,
    noOfMcqm,
    noOfFib,
    noOfTf,
    noOfVeryShort,
    noOfShort,
    noOfLong,
    noOfCbq,
  } = noOfQuestions;

  // ----------------------------------------------------

  const getNoOfQuestions = (number?: number) => {
    return qpg_settings_config?.allow_ai_questions
      ? MAX_AI_QUESTIONS
      : number || 0;
  };

  // ----------------------------------------------------

  const questionTypeList: IOptionList[] = [
    {
      name: 'MCQ Single',
      nonEditableItem: !!current_qpg_template_info?.questionSectionInfo?.find(
        (e) => e.questionType === Question_QuestionEnum.QUESTION_TYPE_MCQS
      ),
      marks: qpg_settings_config?.question_type_marks?.find(
        (e) => e.questionType === Question_QuestionEnum.QUESTION_TYPE_MCQS
      )?.marks,
      noOfQuestions: getNoOfQuestions(noOfMcqs),
      questionType: Question_QuestionEnum.QUESTION_TYPE_MCQS,
    },
    {
      name: 'MCQ Multiple',
      nonEditableItem: !!current_qpg_template_info?.questionSectionInfo?.find(
        (e) => e.questionType === Question_QuestionEnum.QUESTION_TYPE_MCQM
      ),
      marks: qpg_settings_config?.question_type_marks?.find(
        (e) => e.questionType === Question_QuestionEnum.QUESTION_TYPE_MCQM
      )?.marks,
      noOfQuestions: getNoOfQuestions(noOfMcqm),
      questionType: Question_QuestionEnum.QUESTION_TYPE_MCQM,
    },
    {
      name: 'Fill in the Blanks',
      nonEditableItem: !!current_qpg_template_info?.questionSectionInfo?.find(
        (e) => e.questionType === Question_QuestionEnum.QUESTION_TYPE_FIB
      ),
      marks: qpg_settings_config?.question_type_marks?.find(
        (e) => e.questionType === Question_QuestionEnum.QUESTION_TYPE_FIB
      )?.marks,
      noOfQuestions: getNoOfQuestions(noOfFib),
      questionType: Question_QuestionEnum.QUESTION_TYPE_FIB,
    },
    {
      name: 'True/False',
      nonEditableItem: !!current_qpg_template_info?.questionSectionInfo?.find(
        (e) => e.questionType === Question_QuestionEnum.QUESTION_TYPE_TF
      ),
      marks: qpg_settings_config?.question_type_marks?.find(
        (e) => e.questionType === Question_QuestionEnum.QUESTION_TYPE_TF
      )?.marks,
      noOfQuestions: getNoOfQuestions(noOfTf),
      questionType: Question_QuestionEnum.QUESTION_TYPE_TF,
    },
    {
      name: 'Very Short',
      nonEditableItem: !!current_qpg_template_info?.questionSectionInfo?.find(
        (e) => e.questionType === Question_QuestionEnum.QUESTION_TYPE_VERY_SHORT
      ),
      marks: qpg_settings_config?.question_type_marks?.find(
        (e) => e.questionType === Question_QuestionEnum.QUESTION_TYPE_VERY_SHORT
      )?.marks,
      noOfQuestions: getNoOfQuestions(noOfVeryShort),
      questionType: Question_QuestionEnum.QUESTION_TYPE_VERY_SHORT,
    },
    {
      name: 'Short',
      nonEditableItem: !!current_qpg_template_info?.questionSectionInfo?.find(
        (e) => e.questionType === Question_QuestionEnum.QUESTION_TYPE_SHORT
      ),
      marks: qpg_settings_config?.question_type_marks?.find(
        (e) => e.questionType === Question_QuestionEnum.QUESTION_TYPE_SHORT
      )?.marks,
      noOfQuestions: getNoOfQuestions(noOfShort),
      questionType: Question_QuestionEnum.QUESTION_TYPE_SHORT,
    },
    {
      name: 'Long Answer',
      nonEditableItem: !!current_qpg_template_info?.questionSectionInfo?.find(
        (e) => e.questionType === Question_QuestionEnum.QUESTION_TYPE_LONG
      ),
      marks: qpg_settings_config?.question_type_marks?.find(
        (e) => e.questionType === Question_QuestionEnum.QUESTION_TYPE_LONG
      )?.marks,
      noOfQuestions: getNoOfQuestions(noOfLong),
      questionType: Question_QuestionEnum.QUESTION_TYPE_LONG,
    },
    {
      name: 'Competency Based',
      nonEditableItem: !!current_qpg_template_info?.questionSectionInfo?.find(
        (e) => e.questionType === Question_QuestionEnum.QUESTION_TYPE_CBQ
      ),
      marks: qpg_settings_config?.question_type_marks?.find(
        (e) => e.questionType === Question_QuestionEnum.QUESTION_TYPE_CBQ
      )?.marks,
      noOfQuestions: getNoOfQuestions(noOfCbq),
      questionType: Question_QuestionEnum.QUESTION_TYPE_CBQ,
    },
  ].filter(
    (e) =>
      e.noOfQuestions &&
      e.noOfQuestions > 0 &&
      ((remainingByType.get(e.questionType!) ?? 0) > 0 ||
        subSectionDetails.subSectionQuestions.some(
          (q) => q.questionType === e.questionType
        ))
  );

  // ----------------------------------------------------

  const updateQuestionTypeMarks = (
    questionType: Question_QuestionEnum,
    marks: number
  ) => {
    const updatedQuestionTypeMarks = new QuestionTypeMarks({
      questionType: questionType,
      marks: marks,
    });
    const updatedQuestionTypeMarksList =
      qpg_settings_config?.question_type_marks?.filter(
        (e) => e.questionType !== questionType
      ) || [];
    updatedQuestionTypeMarksList.push(updatedQuestionTypeMarks);
    dispatch(
      setQPGSettingsConfig({
        ...qpg_settings_config,
        question_type_marks: updatedQuestionTypeMarksList,
      })
    );
  };

  // ----------------------------------------------------

  const syncQpgContentFromSubsectionData = (
    updatedSubSectionData: SubSectionType[]
  ) => {
    const sectionIndex = qpg_selected_section_index ?? 0;
    if (!qpg_content?.multiSubjectSections?.[sectionIndex]) return;
    const subsection = updatedSubSectionData[subSectionIndex];
    if (!subsection?.subSectionQuestions?.length) return;

    const sections = [...qpg_content.multiSubjectSections];
    const selectedSection = cloneObject(
      qpg_content.multiSubjectSections[sectionIndex]
    );
    const subSections = [...(selectedSection.multiSubjectSubSections ?? [])];
    const targetSub = subSections[subSectionIndex];
    if (!targetSub) return;

    const subSectionMetaData = subsection.subSectionQuestions.map((q, i) => {
      const existing = targetSub.subSectionMetaData?.[i];
      return new SectionQuestionMetaData({
        questionId: existing?.questionId ?? '',
        alternateQuestions: existing?.alternateQuestions ?? [],
        sequence: existing?.sequence ?? i + 1,
        showMedia: existing?.showMedia ?? false,
        isAiGenerated: existing?.isAiGenerated ?? false,
        maxMarks: q.marks ?? 0,
      });
    });
    const questionTypeAndMarks = subsection.subSectionQuestions.map(
      (q) =>
        new QuestionTypeMarks({
          questionType:
            q.questionType ?? Question_QuestionEnum.QUESTION_TYPE_UNDEFINED,
          marks: q.marks ?? 0,
        })
    );
    const updatedTargetSub = new MultiSubjectSubSection({
      ...targetSub,
      subSectionMetaData,
      questionTypeAndMarks,
    });
    subSections[subSectionIndex] = updatedTargetSub;
    selectedSection.multiSubjectSubSections = subSections;
    sections[sectionIndex] = selectedSection;
    const updatedContent = cloneObject(qpg_content);
    updatedContent.multiSubjectSections = sections;
    dispatch(setQPGContent(updatedContent));
  };

  // ----------------------------------------------------

  const updateSubSectionRowNoOfQuestions = (
    rowIndex: number,
    nextCount: number
  ) => {
    const currentSubSectionData = qpg_subsection_data ?? [];
    const subsectionAtIdx = currentSubSectionData[subSectionIndex];
    if (!subsectionAtIdx) return;

    const updatedSubSectionQuestions = subsectionAtIdx.subSectionQuestions.map(
      (e, i) =>
        i === rowIndex
          ? new SubSectionQuestionType({
              ...e,
              noOfQuestion: nextCount,
            })
          : e
    );
    const updatedSubSectionData = currentSubSectionData.map((sub, i) =>
      i === subSectionIndex
        ? new SubSectionType({
            ...sub,
            subSectionQuestions: updatedSubSectionQuestions,
          })
        : sub
    );
    dispatch(setQPGSubsectionData(updatedSubSectionData));
    syncQpgContentFromSubsectionData(updatedSubSectionData);
  };

  // ----------------------------------------------------

  const getMaxMarksLimit = (questionType: Question_QuestionEnum) =>
    questionType === Question_QuestionEnum.QUESTION_TYPE_UNDEFINED
      ? 0
      : MAX_MARKS_LIMIT;

  // ----------------------------------------------------

  const clampMarks = (marks: number, questionType: Question_QuestionEnum) => {
    if (questionType === Question_QuestionEnum.QUESTION_TYPE_UNDEFINED) {
      return 0;
    }
    return Math.min(Math.max(0, marks), MAX_MARKS_LIMIT);
  };

  // ----------------------------------------------------

  const getCurrentMarksForRow = (queDetails: SubSectionQuestionType) => {
    const marks =
      queDetails.marks ??
      qpg_settings_config?.question_type_marks?.find(
        (e) => e.questionType === queDetails.questionType
      )?.marks ??
      0;
    return clampMarks(marks, queDetails.questionType);
  };

  // ----------------------------------------------------

  const updateSubSectionRowMarks = (
    rowIndex: number,
    nextMarks: number,
    questionType: Question_QuestionEnum
  ) => {
    const clampedMarks = clampMarks(nextMarks, questionType);
    const currentSubSectionData = qpg_subsection_data ?? [];
    const subsectionAtIdx = currentSubSectionData[subSectionIndex];
    if (!subsectionAtIdx) return;

    const updatedSubSectionQuestions = subsectionAtIdx.subSectionQuestions.map(
      (e, i) =>
        i === rowIndex
          ? new SubSectionQuestionType({
              ...e,
              marks: clampedMarks,
            })
          : e
    );
    const updatedSubSectionData = currentSubSectionData.map((sub, i) =>
      i === subSectionIndex
        ? new SubSectionType({
            ...sub,
            subSectionQuestions: updatedSubSectionQuestions,
          })
        : sub
    );
    dispatch(setQPGSubsectionData(updatedSubSectionData));
    syncQpgContentFromSubsectionData(updatedSubSectionData);

    if (questionType !== Question_QuestionEnum.QUESTION_TYPE_UNDEFINED) {
      updateQuestionTypeMarks(questionType, clampedMarks);
    }
  };

  // ----------------------------------------------------

  const getTotalMarks = () => {
    return subSectionDetails.subSectionQuestions.reduce((acc, curr) => {
      return acc + (curr.noOfQuestion || 0) * (curr.marks || 0);
    }, 0);
  };

  // ----------------------------------------------------

  const getTotalQuestions = () => {
    return subSectionDetails.subSectionQuestions.reduce((acc, curr) => {
      return acc + (curr.noOfQuestion || 0);
    }, 0);
  };

  // ----------------------------------------------------

  const deleteRowHandler = (rowIndex: number) => {
    const currentSubSectionData = qpg_subsection_data ?? [];
    const subsectionAtIdx = currentSubSectionData[subSectionIndex];
    if (!subsectionAtIdx?.subSectionQuestions?.length) return;

    const updatedSubSectionQuestions =
      subsectionAtIdx.subSectionQuestions.filter((_, i) => i !== rowIndex);
    const updatedSubSectionData = currentSubSectionData.map((sub, i) =>
      i === subSectionIndex
        ? new SubSectionType({
            ...sub,
            subSectionQuestions: updatedSubSectionQuestions,
          })
        : sub
    );
    dispatch(setQPGSubsectionData(updatedSubSectionData));

    const sectionIndex = qpg_selected_section_index ?? 0;
    if (qpg_content?.multiSubjectSections?.[sectionIndex]) {
      const sections = [...qpg_content.multiSubjectSections];
      const selectedSection = cloneObject(
        qpg_content.multiSubjectSections[sectionIndex]
      );
      const subSections = [...(selectedSection.multiSubjectSubSections ?? [])];
      const targetSub = subSections[subSectionIndex];
      if (targetSub) {
        const newMetaData = (targetSub.subSectionMetaData ?? []).filter(
          (_, i) => i !== rowIndex
        );
        const newQuestionTypeAndMarks = (
          targetSub.questionTypeAndMarks ?? []
        ).filter((_, i) => i !== rowIndex);
        subSections[subSectionIndex] = new MultiSubjectSubSection({
          ...targetSub,
          subSectionMetaData: newMetaData,
          questionTypeAndMarks: newQuestionTypeAndMarks,
        });
        selectedSection.multiSubjectSubSections = subSections;
        sections[sectionIndex] = selectedSection;
        const updatedContent = cloneObject(qpg_content);
        updatedContent.multiSubjectSections = sections;
        dispatch(setQPGContent(updatedContent));
      }
    }
  };

  // ----------------------------------------------------

  const canAddMoreQuestionType = () => {
    if (!areAllSubSectionQuestionsFilled(subSectionDetails.subSectionQuestions))
      return false;
    if (allowAiQuestions) {
      return questionTypeList.some(
        (e) => (remainingByType.get(e.questionType!) ?? 0) > 0
      );
    }
    const selectedTypes = new Set(
      subSectionDetails.subSectionQuestions
        .map((q) => q.questionType)
        .filter(
          (t): t is Question_QuestionEnum =>
            t !== undefined &&
            t !== Question_QuestionEnum.QUESTION_TYPE_UNDEFINED
        )
    );
    return selectedTypes.size < questionTypeList.length;
  };

  // ----------------------------------------------------

  /* Question types already selected in other rows of this subsection
   (so we can disable them in the dropdown) */
  const getQuestionTypesUsedByOtherRows = (
    currentRowIndex: number
  ): Set<Question_QuestionEnum> =>
    new Set(
      subSectionDetails.subSectionQuestions
        .map((q, i) => (i !== currentRowIndex ? q.questionType : undefined))
        .filter(
          (t): t is Question_QuestionEnum =>
            t !== undefined &&
            t !== Question_QuestionEnum.QUESTION_TYPE_UNDEFINED
        )
    );

  // ----------------------------------------------------

  return (
    <Box sx={style.root}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
        <Box sx={style.headerReorderBox}>
          <IconWrapper
            name="reorder"
            type="png"
            parentFolder="icons"
            customSx={style.reorderIcon}
          />
          {/* <Typography
            variant={isMobile ? 'h2' : 'h1'}
            fontWeight="bold"
          >{`Sub Section ${index + 1} ${numberToAlphabet(
            index + 1
          )}`}</Typography> */}
          <Typography variant={isMobile ? 'h2' : 'h1'} fontWeight="bold">
            {subSectionDetails.subSectionName}
          </Typography>
        </Box>

        <Box sx={style.marksAndDeleteBox}>
          {getTotalQuestions() > 0 && (
            <Box sx={style.marksDisplayBox}>
              <Typography variant="cardText">
                {getTotalMarks()} Marks
              </Typography>
            </Box>
          )}
          <IconWrapper
            name="delete2"
            type="png"
            parentFolder="icons"
            onClick={async () => {
              deleteSection(subSectionIndex);
              // updateQuestionTypeMarks(section.questionType, 1);
              // await interactionEvent({
              //   context: 'Section',
              //   name: 'DELETE',
              //   pathSegments: pathSegments,
              //   isOffline: isOffline,
              // });
            }}
            customSx={style.deleteIcon}
          />
        </Box>
      </Box>

      {subSectionDetails.subSectionQuestions.map((queDetails, index) => (
        <Box
          key={index}
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: { xs: pxToRem(10), md: pxTovW(20) },
          }}
        >
          <Box sx={style.questionTypeAndDetailsBox}>
            <Box sx={style.questionDetailsBox}>
              <Box sx={style.questionTypeBox}>
                <Typography variant="h4" fontWeight="bold">
                  Choose Question Type
                </Typography>
                <SelectMenu
                  parentStyles={{ width: { xs: '100%', md: 'unset' } }}
                  placeholder="Select Question Type"
                  value={
                    questionTypeList.find(
                      (e) => e.questionType === queDetails.questionType
                    )?.name || ''
                  }
                  renderValue={(selected, placeholder) => {
                    const questionTypeFound = questionTypeList.find(
                      (e) => e.name === selected
                    );
                    return !questionTypeFound ? (
                      <Box sx={style.selectMenuPlaceholderBox}>
                        {placeholder}
                      </Box>
                    ) : (
                      <Box sx={style.selectMenuValueBox}>
                        {questionTypeFound.name}
                        {/* <Typography
                        variant="cardText"
                        sx={{
                          color: '#0078D5',
                          pl: { xs: pxToRem(6), md: pxTovW(6) },
                        }}
                      >
                        {questionTypeFound.marks} Marks
                      </Typography> */}
                      </Box>
                    );
                  }}
                  optionList={questionTypeList.map((e, optionIndex) => {
                    const usedByOtherRows =
                      getQuestionTypesUsedByOtherRows(index);
                    const remaining = remainingByType.get(e.questionType!);
                    const exhaustedAndNotSelected =
                      remaining === 0 &&
                      queDetails.questionType !== e.questionType;
                    return {
                      name: e.name,
                      id: optionIndex + 1,
                      nonEditableItem:
                        e.nonEditableItem ||
                        usedByOtherRows.has(e.questionType!) ||
                        exhaustedAndNotSelected,
                    };
                  })}
                  onChange={async (value: string) => {
                    const questionTypeFound = questionTypeList.find(
                      (e) => e.name === value
                    );
                    // if (questionTypeFound) {
                    //   const updatedQuestionSection = new QuestionSection({
                    //     ...section,
                    //     noOfQuestion: 0,
                    //     marks: questionTypeFound.marks || 0,
                    //     questionType:
                    //       questionTypeFound.questionType ||
                    //       Question_QuestionEnum.QUESTION_TYPE_UNDEFINED,
                    //   });
                    //   updateSection(index, updatedQuestionSection);
                    // }

                    const currentSubSectionData = qpg_subsection_data ?? [];
                    const subsectionAtIdx =
                      currentSubSectionData[subSectionIndex];
                    if (subsectionAtIdx) {
                      const updatedSubSectionQuestions =
                        subsectionAtIdx.subSectionQuestions.map((e, i) =>
                          i === index
                            ? new SubSectionQuestionType({
                                ...e,
                                questionType:
                                  questionTypeFound?.questionType ??
                                  Question_QuestionEnum.QUESTION_TYPE_UNDEFINED,
                                noOfQuestion: 0,
                              })
                            : e
                        );
                      const updatedSubSectionData = currentSubSectionData.map(
                        (sub, i) =>
                          i === subSectionIndex
                            ? new SubSectionType({
                                ...sub,
                                subSectionQuestions: updatedSubSectionQuestions,
                              })
                            : sub
                      );
                      dispatch(setQPGSubsectionData(updatedSubSectionData));
                      syncQpgContentFromSubsectionData(updatedSubSectionData);
                    }

                    // await interactionEvent({
                    //   context: 'Select_question_type',
                    //   name: value
                    //     .toUpperCase()
                    //     .replace(/\s+/g, '_')
                    //     .replace(/\//g, '_/_'),
                    //   pathSegments: pathSegments,
                    //   isOffline: isOffline,
                    // });
                  }}
                  rootStyles={style.selectMenuRootStyles}
                />
              </Box>

              <Box sx={style.marksAndNoOfQuestionsBox}>
                <Box sx={style.marksBox}>
                  <Typography
                    variant="h4"
                    fontWeight="bold"
                    marginRight={'auto'}
                    width={'max-content'}
                  >
                    Add Marks
                  </Typography>
                  <QuantityButtonV2
                    value={getCurrentMarksForRow(queDetails)}
                    max={getMaxMarksLimit(queDetails.questionType)}
                    disabled={getMaxMarksLimit(queDetails.questionType) === 0}
                    onChange={(nextValue: number) => {
                      updateSubSectionRowMarks(
                        index,
                        nextValue,
                        queDetails.questionType
                      );
                    }}
                    onIncrement={() => {
                      const currentMarks = getCurrentMarksForRow(queDetails);
                      if (currentMarks < MAX_MARKS_LIMIT) {
                        updateSubSectionRowMarks(
                          index,
                          currentMarks + 1,
                          queDetails.questionType
                        );
                      }
                    }}
                    onDecrement={() => {
                      const currentMarks = getCurrentMarksForRow(queDetails);
                      if (currentMarks > 0) {
                        updateSubSectionRowMarks(
                          index,
                          Math.max(0, currentMarks - 1),
                          queDetails.questionType
                        );
                      }
                    }}
                  />
                </Box>

                <Box sx={style.noOfQuestionsBox}>
                  <Typography
                    variant="h4"
                    fontWeight="bold"
                    marginRight={'auto'}
                    width={'max-content'}
                  >
                    No. of Questions
                  </Typography>
                  <QuantityButtonV2
                    value={queDetails.noOfQuestion ?? 0}
                    max={getMaxNoOfQuestionsLimit(
                      queDetails.questionType,
                      index
                    )}
                    disabled={
                      getMaxNoOfQuestionsLimit(
                        queDetails.questionType,
                        index
                      ) === 0
                    }
                    onChange={(nextValue: number) => {
                      updateSubSectionRowNoOfQuestions(index, nextValue);
                    }}
                    onIncrement={() => {
                      const maxCount = getMaxNoOfQuestionsLimit(
                        queDetails.questionType,
                        index
                      );
                      const currentCount = queDetails.noOfQuestion ?? 0;
                      if (currentCount < maxCount) {
                        updateSubSectionRowNoOfQuestions(
                          index,
                          currentCount + 1
                        );
                      }
                    }}
                    onDecrement={() => {
                      const currentCount = queDetails.noOfQuestion ?? 0;
                      if (currentCount > 0) {
                        updateSubSectionRowNoOfQuestions(
                          index,
                          currentCount - 1
                        );
                      }
                    }}
                  />
                </Box>
              </Box>
            </Box>

            <Box sx={style.alternateAndDeleteBox}>
              <Box sx={style.addAlternateQuestionsBox}>
                <Checkbox
                  checked={queDetails.isAlternateEnable}
                  onChange={async (e) => {
                    // const updatedQuestionSection = new QuestionSection({
                    //   ...queDetails,
                    //   isAlternateEnable: e.target.checked,
                    // });
                    // updateSection(index, updatedQuestionSection);

                    const currentSubSectionData = qpg_subsection_data ?? [];
                    const subsectionAtIdx =
                      currentSubSectionData[subSectionIndex];
                    if (subsectionAtIdx) {
                      const updatedSubSectionQuestions =
                        subsectionAtIdx.subSectionQuestions.map((item, i) =>
                          i === index
                            ? new SubSectionQuestionType({
                                ...item,
                                isAlternateEnable: e.target.checked,
                              })
                            : item
                        );
                      const updatedSubSectionData = currentSubSectionData.map(
                        (sub, i) =>
                          i === subSectionIndex
                            ? new SubSectionType({
                                ...sub,
                                subSectionQuestions: updatedSubSectionQuestions,
                              })
                            : sub
                      );
                      dispatch(setQPGSubsectionData(updatedSubSectionData));
                      syncQpgContentFromSubsectionData(updatedSubSectionData);
                    }

                    // await interactionEvent({
                    //   context: 'Add_alternate_questions',
                    //   name: e.target.checked ? 'SELECTED' : 'UNSELECTED',
                    //   pathSegments: pathSegments,
                    //   isOffline: isOffline,
                    // });
                  }}
                  sx={style.alternateQuestionsCheckbox}
                  color="primary"
                />
                <Typography variant="h4" sx={style.alternateQuestionsTypo}>
                  Add Alternate Questions
                </Typography>
              </Box>
            </Box>
          </Box>

          {subSectionDetails.subSectionQuestions.length > 1 && (
            <IconWrapper
              name="delete2"
              type="png"
              parentFolder="icons"
              onClick={() => deleteRowHandler(index)}
              customSx={style.rowDeleteIcon}
            />
          )}
        </Box>
      ))}

      <Typography
        variant="linkText"
        color="primary"
        textAlign="center"
        onClick={() => {
          if (canAddMoreQuestionType()) {
            addMoreHandler(subSectionIndex);
          }
        }}
        sx={{
          marginTop: { xs: pxToRem(10), md: pxTovW(20) },
          fontFamily: 'poppins',
          fontSize: { xs: pxToRem(21), md: pxTovW(21) },
          cursor: canAddMoreQuestionType() ? 'pointer' : 'not-allowed',
          color: canAddMoreQuestionType() ? undefined : 'grey',
          opacity: canAddMoreQuestionType() ? 1 : 0.6,
        }}
      >
        + Add More
      </Typography>
    </Box>
  );
};

// ==========================================================
